import React, { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PencilIcon, Save, X, Settings, Layers, Eye, EyeOff } from 'lucide-react';
import { useEffectiveAttributes } from '@/hooks/useEffectiveAttributes';
import { useUpdateAttribute, useCreateAttribute } from './api';
import type { EffectiveAttribute } from '@/services/productService';
import LocaleChannelSelector from '@/features/attributes/LocaleChannelSelector';
import { toast } from 'sonner';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChannelCode, LocaleCode } from '@/services/types';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { useTranslation } from 'react-i18next';
import { translateAttributeType } from '@/utils/attributeTypeTranslations';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { formatDateForDisplay, DateFormat } from '@/utils/numberFormat';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { logDebug, logError } from '@/utils/errorHandler';
import { Switch } from '@/components/ui/switch';

interface EffectiveAttributesViewProps {
  productId: string;
  locale?: string;
  channel?: string;
  enabled?: boolean;
}

// Helper function to format date values for date inputs
function formatDateForInput(value: any): string {
  if (!value) return '';

  // If it's already in YYYY-MM-DD format, return as-is
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // If it's an ISO datetime string, extract just the date part without timezone conversion
  if (typeof value === 'string') {
    // For ISO strings like "2025-01-15T00:00:00Z" or "2025-01-15T10:30:00.000Z"
    // Just extract the date part directly without creating a Date object
    const isoMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) {
      return isoMatch[1];
    }

    // If it's not an ISO string, try to parse it with Date but use local methods
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      // Use local date methods to avoid timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }

  return '';
}

// Static fallback: measurement units (replace with API-driven if available)
// const units = ['mm', 'cm', 'm', 'in', 'ft', 'kg', 'g', 'lb', 'oz', 'l', 'ml']; // Currently unused

export function EffectiveAttributesView({
  productId,
  locale,
  channel,
  enabled = true,
}: EffectiveAttributesViewProps) {
  // Initialize translations
  const { getAttributesLabels } = useProductDetailPageTranslations();
  const { t, i18n, ready } = useTranslation('translation');

  // Debug translations
  React.useEffect(() => {
    logDebug('Translation Debug Info - EffectiveAttributesView', {
      currentLanguage: i18n.language,
      translationReady: ready,
      availableLanguages: Object.keys(i18n.store.data),
      spanishDataAvailable: !!i18n.store.data.es,
    });

    if (i18n.store.data.es) {
      const spanishData = i18n.store.data.es as any;
      if (spanishData.translation) {
        logDebug('Spanish translation data', {
          translationKeys: Object.keys(spanishData.translation),
          dataTypesAvailable: !!spanishData.translation.attributes?.enhanced?.modal?.dataTypes,
        });
      }
    }

    // Test specific translations
    const testResult = t('attributes.enhanced.modal.dataTypes.measurement.label');
    logDebug('Test translation result', { testResult });
  }, [i18n, ready, t]);

  // Get organization settings for date formatting and currencies
  const { orgSettings, currencies } = useOrgSettings();
  const organizationDateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  const [selectedLocale, setSelectedLocale] = useState<LocaleCode>(locale ?? 'en_US');
  const [selectedChannel, setSelectedChannel] = useState<ChannelCode>(channel ?? '');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<any>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaUploadError, setMediaUploadError] = useState<string | undefined>(undefined);

  // Query client for manual cache invalidation
  const queryClient = useQueryClient();

  // Fetch effective attributes
  const {
    data: groups,
    isLoading,
    error,
    refetch,
  } = useEffectiveAttributes(parseInt(productId), selectedLocale, selectedChannel, enabled);

  // Collect all select attribute IDs for fetching options
  const selectAttributeIds = React.useMemo(() => {
    if (!groups) return [];
    return groups.flatMap(group =>
      group.attributes.filter(attr => attr.data_type === 'select').map(attr => attr.id)
    );
  }, [groups]);

  // Fetch options for all select attributes
  const selectAttributeQueries = useQueries({
    queries: selectAttributeIds.map(attributeId => ({
      queryKey: ['attributeOptions', attributeId],
      queryFn: async () => {
        const response = await axiosInstance.get(`/api/attributes/${attributeId}/`);
        return { attributeId, options: response.data.options || [] };
      },
      enabled: Boolean(attributeId),
      staleTime: 10 * 60 * 1000, // 10 minutes
    })),
  });

  // Create a map of attribute ID to options for easy lookup
  const attributeOptionsMap = React.useMemo(() => {
    const map = new Map<number, any[]>();
    selectAttributeQueries.forEach(query => {
      if (query.data) {
        map.set(query.data.attributeId, query.data.options);
      }
    });
    return map;
  }, [selectAttributeQueries]);

  // Mutations for editing (still use existing write paths)
  const updateMutation = useUpdateAttribute(productId, selectedLocale, selectedChannel);

  const createMutation = useCreateAttribute(productId, selectedLocale, selectedChannel);

  // Helper to upload media and return asset_id
  async function uploadMedia(file: File): Promise<number> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('file_size', file.size.toString());
    formData.append('content_type', file.type);

    // Determine asset type from file type
    const mime = file.type.toLowerCase();
    let assetType = 'other';
    if (mime.startsWith('image/')) assetType = 'image';
    else if (mime.includes('pdf')) assetType = 'pdf';
    else if (mime.match(/(xlsx|xls|csv)/)) assetType = 'spreadsheet';
    else if (mime.match(/(doc|docx|text)/)) assetType = 'document';

    formData.append('asset_type', assetType);

    try {
      const response = await axiosInstance.post(`/api/products/${productId}/assets/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status !== 201 && response.status !== 200) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      const data = response.data;

      // Invalidate assets cache for this product
      queryClient.invalidateQueries({ queryKey: ['assets', productId] });
      queryClient.invalidateQueries({
        queryKey: ['product', parseInt(productId)],
      });

      return data.id; // Return the asset ID
    } catch (error) {
      logError(
        'Media upload error',
        {
          component: 'EffectiveAttributesView',
          action: 'uploadMedia',
          metadata: { error, fileName: file.name, fileSize: file.size },
        },
        { showToast: false, userFacing: false }
      );
      if (error instanceof Error) {
        throw new Error(`Upload failed: ${error.message}`);
      }
      throw new Error('Upload failed');
    }
  }

  // Type-aware attribute editor for each attribute type
  function renderAttributeEditor(
    attr: EffectiveAttribute,
    draftValue: any,
    setDraftValue: (v: any) => void,
    isDisabled: boolean,
    validationError?: string
  ) {
    const type = attr.data_type;
    switch (type) {
      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={Boolean(draftValue)}
              onCheckedChange={setDraftValue}
              disabled={isDisabled}
            />
            <span className="text-sm">
              {draftValue
                ? t('settingspage.attributeValueRow.labels.boolean.yes', 'Yes')
                : t('settingspage.attributeValueRow.labels.boolean.no', 'No')}
            </span>
          </div>
        );
      case 'rich_text':
        return <RichTextEditor value={draftValue || ''} onChange={setDraftValue} />;
      case 'price':
        return (
          <div className="flex gap-2">
            <Input
              type="number"
              step="0.01"
              value={draftValue?.amount ?? ''}
              onChange={e => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setDraftValue({ ...draftValue, amount: '' });
                } else {
                  const numValue = parseFloat(inputValue);
                  setDraftValue({
                    ...draftValue,
                    amount: isNaN(numValue) ? '' : numValue,
                  });
                }
              }}
              disabled={isDisabled}
              placeholder={getAttributesLabels.placeholders.amount}
              className="w-24"
            />
            <Select
              value={draftValue?.currency || ''}
              onValueChange={c => setDraftValue({ ...draftValue, currency: c })}
              disabled={isDisabled}
            >
              <SelectTrigger className="w-28">
                <SelectValue placeholder={getAttributesLabels.placeholders.currency} />
              </SelectTrigger>
              <SelectContent>
                {(currencies || []).map(c => (
                  <SelectItem key={c.iso_code} value={c.iso_code}>
                    {c.iso_code} ({c.symbol})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case 'measurement':
        return (
          <div className="flex gap-2">
            <Input
              type="number"
              value={draftValue?.amount ?? ''}
              onChange={e =>
                setDraftValue({
                  ...draftValue,
                  amount: parseFloat(e.target.value),
                })
              }
              disabled={isDisabled}
              placeholder={getAttributesLabels.placeholders.amount}
              className="w-24"
            />
            <Select
              value={draftValue?.unit || ''}
              onValueChange={u => setDraftValue({ ...draftValue, unit: u })}
              disabled={isDisabled}
            >
              <SelectTrigger className="w-20">
                <SelectValue placeholder={getAttributesLabels.placeholders.unit} />
              </SelectTrigger>
              <SelectContent>
                {(attr.allowed_units || []).map(u => (
                  <SelectItem key={u} value={u}>
                    {u}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case 'phone':
        return (
          <div>
            <Input
              type="tel"
              value={draftValue || ''}
              onChange={e => setDraftValue(e.target.value)}
              disabled={isDisabled}
              placeholder={getAttributesLabels.placeholders.phoneInput}
              pattern="^\\+?[0-9\-\s]{7,}$"
            />
            <div className="text-xs text-muted-foreground">
              {getAttributesLabels.panel.formatHints.phone}
            </div>
            {validationError && <div className="text-xs text-red-500">{validationError}</div>}
          </div>
        );
      case 'email':
        return (
          <div>
            <Input
              type="email"
              value={draftValue || ''}
              onChange={e => setDraftValue(e.target.value)}
              disabled={isDisabled}
              placeholder={getAttributesLabels.placeholders.emailInput}
              pattern="^[^@\s]+@[^@\s]+\\.[^@\s]+$"
            />
            <div className="text-xs text-muted-foreground">
              {getAttributesLabels.panel.formatHints.email}
            </div>
            {validationError && <div className="text-xs text-red-500">{validationError}</div>}
          </div>
        );
      case 'url':
        return (
          <div>
            <Input
              type="url"
              value={draftValue || ''}
              onChange={e => setDraftValue(e.target.value)}
              disabled={isDisabled}
              placeholder={getAttributesLabels.placeholders.urlInput}
              pattern="^(https?:)?//"
            />
            <div className="text-xs text-muted-foreground">
              {getAttributesLabels.panel.formatHints.url}
            </div>
            {validationError && <div className="text-xs text-red-500">{validationError}</div>}
          </div>
        );
      case 'date':
        return (
          <div>
            <Input
              type="date"
              value={formatDateForInput(draftValue)}
              onChange={e => setDraftValue(e.target.value)}
              disabled={isDisabled}
              placeholder={t(
                'productdetailpage.productAttributes.panel.formatHints.date',
                'Pick a date (YYYY-MM-DD)'
              )}
            />
            <div className="text-xs text-muted-foreground">
              {getAttributesLabels.panel.formatHints.date}
            </div>
            {validationError && <div className="text-xs text-red-500">{validationError}</div>}
          </div>
        );
      case 'media':
        return (
          <div>
            <input
              type="file"
              accept="image/*,video/*,application/pdf"
              onChange={async e => {
                const file = e.target.files?.[0];
                if (file) {
                  setMediaUploading(true);
                  setMediaUploadError(undefined);
                  try {
                    const assetId = await uploadMedia(file);
                    setDraftValue({ asset_id: assetId });
                  } catch {
                    setMediaUploadError(getAttributesLabels.errors.uploadError);
                  } finally {
                    setMediaUploading(false);
                  }
                }
              }}
              disabled={isDisabled}
            />
            {draftValue && draftValue.asset_id && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs">
                  {getAttributesLabels.panel.media.assetId.replace('{{id}}', draftValue.asset_id)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDraftValue(undefined)}
                  disabled={isDisabled}
                >
                  {getAttributesLabels.panel.media.remove}
                </Button>
              </div>
            )}
            {mediaUploading && (
              <div className="text-xs text-muted-foreground">
                {getAttributesLabels.panel.media.uploading}
              </div>
            )}
            {mediaUploadError && <div className="text-xs text-red-500">{mediaUploadError}</div>}
          </div>
        );
      case 'select': {
        // Get options from the pre-fetched map
        const options = attributeOptionsMap.get(attr.id) || [];
        const isLoadingOptions = selectAttributeQueries.some(
          query => query.data?.attributeId === attr.id && query.isLoading
        );

        if (isLoadingOptions) {
          return (
            <div className="flex items-center gap-2">
              <div className="size-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
              <span className="text-sm text-muted-foreground">
                {t('productdetailpage.ui.common.loading', 'Loading...')}
              </span>
            </div>
          );
        }

        if (Array.isArray(options) && options.length > 0) {
          return (
            <div>
              <Select
                value={draftValue || ''}
                onValueChange={v => setDraftValue(v)}
                disabled={isDisabled}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={getAttributesLabels.placeholders.selectOption} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt: any) => (
                    <SelectItem key={opt.value ?? opt.id ?? opt} value={opt.value ?? opt.id ?? opt}>
                      {opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }

        // Fallback: no options available - show message instead of input
        return (
          <div className="text-sm italic text-muted-foreground">
            No options configured for this select attribute
          </div>
        );
      }
      case 'number':
        return (
          <Input
            type="number"
            value={draftValue ?? ''}
            onChange={e => setDraftValue(e.target.value)}
            disabled={isDisabled}
            placeholder={getAttributesLabels.placeholders.numberInput}
          />
        );
      default:
        return (
          <Input
            value={draftValue ?? ''}
            onChange={e => setDraftValue(e.target.value)}
            disabled={isDisabled}
            placeholder={getAttributesLabels.placeholders.textInput}
          />
        );
    }
  }

  // Helper to format attribute values for display in the table
  function formatAttributeValue(attr: EffectiveAttribute): string {
    const type = attr.data_type;
    const value = attr.value;
    if (value === null || value === undefined || value === '') return '—';
    switch (type) {
      case 'boolean':
        return value
          ? t('settingspage.attributeValueRow.labels.boolean.yes', 'Yes')
          : t('settingspage.attributeValueRow.labels.boolean.no', 'No');
      case 'price':
        if (typeof value === 'object' && value.amount != null && value.currency) {
          return `${value.amount} ${value.currency}`;
        }
        return '—';
      case 'measurement':
        if (typeof value === 'object' && value.amount != null) {
          // Use provided unit or fall back to attribute's default unit
          const displayUnit = value.unit || attr.default_unit || '';
          return displayUnit ? `${value.amount} ${displayUnit}` : `${value.amount}`;
        }
        return '—';
      case 'media':
        if (typeof value === 'object' && value.name) {
          const fileName = value.name;
          const fileSize = value.file_size;

          // Format file size in human readable format
          let sizeText = '';
          if (fileSize) {
            const size = parseInt(fileSize.toString());
            if (size < 1024) {
              sizeText = ` (${size} B)`;
            } else if (size < 1024 * 1024) {
              sizeText = ` (${Math.round(size / 1024)} KB)`;
            } else {
              sizeText = ` (${Math.round(size / (1024 * 1024))} MB)`;
            }
          }

          return `${fileName}${sizeText}`;
        }
        return '—';
      case 'rich_text':
        return typeof value === 'string' ? value.replace(/<[^>]+>/g, '').slice(0, 80) : '—';
      case 'date':
        // Format date values for display using organization's date format preference
        if (typeof value === 'string') {
          const formattedDate = formatDateForInput(value);
          if (formattedDate) {
            // Parse the YYYY-MM-DD string directly without timezone conversion
            const [year, month, day] = formattedDate.split('-').map(Number);
            const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
            if (!isNaN(date.getTime())) {
              // Use organization's date format preference
              return formatDateForDisplay(date, organizationDateFormat);
            }
          }
        }
        return String(value);
      default:
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
  }

  // Helper to convert technical backend errors to user-friendly messages
  function formatErrorMessage(error: any, attributeType: string): string {
    let errorMessage = '';

    // Extract error message from different error structures
    if (error?.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error?.response?.data?.value?.[0]) {
      errorMessage = error.response.data.value[0];
    } else if (error?.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error?.message) {
      errorMessage = error.message;
    } else {
      errorMessage = String(error);
    }

    // Convert technical messages to user-friendly ones
    const friendlyMessages: Record<string, string> = {
      // Phone errors
      'Missing or invalid default region':
        'Please enter a valid phone number with country code (e.g., +1 555-123-4567) or a US number (e.g., 555-123-4567)',
      'Could not parse phone number':
        'Please enter a valid phone number. Examples: +1 555-123-4567, (555) 123-4567, or 555-123-4567',

      // Email errors
      'Enter a valid email address': 'Please enter a valid email address (e.g., user@example.com)',

      // URL errors
      'Enter a valid URL': 'Please enter a valid website URL (e.g., https://example.com)',

      // Date errors
      'Date has wrong format': 'Please enter a valid date in YYYY-MM-DD format',

      // Number errors
      'A valid integer is required': 'Please enter a valid number',
      'A valid number is required': 'Please enter a valid number',

      // JSON errors
      'Value must be valid JSON': 'Invalid data format. Please check your input.',

      // Generic validation
      'Validation error': 'Please check your input and try again',
      'This field is required': 'This field is required',
    };

    // Check for partial matches in technical messages
    for (const [technical, friendly] of Object.entries(friendlyMessages)) {
      if (errorMessage.toLowerCase().includes(technical.toLowerCase())) {
        return friendly;
      }
    }

    // For unknown errors, return a generic friendly message
    if (
      errorMessage.includes('Error') ||
      errorMessage.includes('error') ||
      errorMessage.includes('Invalid')
    ) {
      return `Please check your ${attributeType} input and try again`;
    }

    return errorMessage;
  }

  const handleSave = async (attr: EffectiveAttribute) => {
    let value = draftValue;
    setValidationError(null);

    // Enhanced validation using central validation API
    if (value !== undefined && value !== null && value !== '') {
      try {
        // Call the central validation API to ensure consistency with backend
        const { validateAttributeValue } = await import('@/services/importService');

        // Only send locale/channel if the attribute supports them
        const validationLocale =
          attr.is_localisable && selectedLocale !== 'default' ? selectedLocale : undefined;
        const validationChannel =
          attr.is_scopable && selectedChannel !== 'default' ? selectedChannel : undefined;

        const validationResponse = await validateAttributeValue(
          attr.id,
          value,
          validationLocale,
          validationChannel
        );

        if (!validationResponse.data.valid) {
          setValidationError(
            formatErrorMessage(validationResponse.data.error || 'Validation failed', attr.data_type)
          );
          return;
        }

        // Use the validated value returned by the API
        if (validationResponse.data.validated_value !== undefined) {
          value = validationResponse.data.validated_value;
        }

        // Show warnings if any
        if (validationResponse.data.warnings && validationResponse.data.warnings.length > 0) {
          validationResponse.data.warnings.forEach((warning: string) => {
            logDebug('Validation warning', { warning, attributeId: attr.id });
            // You could show a toast notification here if desired
          });
        }
      } catch (error) {
        logError(
          'Validation API error',
          {
            component: 'EffectiveAttributesView',
            action: 'handleSave',
            metadata: { error, attributeId: attr.id },
          },
          { showToast: false, userFacing: false }
        );
        // Fall back to basic validation if API fails
        const type = attr.data_type;
        if (type === 'email' && value && !/^\S+@\S+\.\S+$/.test(value)) {
          setValidationError(formatErrorMessage('Invalid email address', type));
          return;
        }
        if (type === 'phone' && value && !/^\+?[0-9\-\s]{7,}$/.test(value)) {
          setValidationError(formatErrorMessage('Invalid phone number format', type));
          return;
        }
        if (type === 'date' && value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          setValidationError(formatErrorMessage('Invalid date format', type));
          return;
        }
        if (type === 'url' && value && value.length > 0 && !/^https?:\/\//i.test(value)) {
          value = 'https://' + value.replace(/^\/*/, '');
        }
      }
    }

    // Format value based on attribute type before sending to API
    const formatValueForAPI = (rawValue: any, dataType: string): any => {
      if (rawValue === '' || rawValue === null || rawValue === undefined) {
        return rawValue;
      }

      switch (dataType) {
        case 'number':
          // Ensure we return an actual number, not a string
          if (typeof rawValue === 'string') {
            const numValue = parseFloat(rawValue);
            return isNaN(numValue) ? 0 : numValue;
          }
          return Number(rawValue) || 0;

        case 'boolean':
          if (typeof rawValue === 'boolean') return rawValue;
          if (typeof rawValue === 'string') {
            const lower = rawValue.toLowerCase().trim();
            return lower === 'true' || lower === '1' || lower === 'yes' || lower === 'on';
          }
          return Boolean(rawValue);

        case 'price':
          if (
            typeof rawValue === 'object' &&
            rawValue !== null &&
            rawValue.amount !== undefined &&
            rawValue.currency !== undefined
          ) {
            // Ensure amount is a valid number
            let amount = 0;
            if (typeof rawValue.amount === 'string') {
              const parsed = parseFloat(rawValue.amount);
              amount = isNaN(parsed) ? 0 : parsed;
            } else if (typeof rawValue.amount === 'number') {
              amount = isNaN(rawValue.amount) ? 0 : rawValue.amount;
            }

            return {
              amount: amount,
              currency: String(rawValue.currency || ''),
            };
          }
          return rawValue;

        case 'measurement':
          if (typeof rawValue === 'object' && rawValue.amount !== undefined) {
            return {
              amount: Number(rawValue.amount) || 0,
              unit: String(rawValue.unit || attr.default_unit || ''),
            };
          }
          return rawValue;

        case 'media':
          if (typeof rawValue === 'object' && rawValue.asset_id !== undefined) {
            return {
              asset_id: Number(rawValue.asset_id) || null,
            };
          }
          return rawValue;

        case 'text':
        case 'rich_text':
        case 'email':
        case 'url':
        case 'phone':
        case 'date':
        case 'select':
          // For text-based fields, ensure we return a string
          return String(rawValue || '');

        default:
          return rawValue;
      }
    };

    // Apply formatting
    value = formatValueForAPI(value, attr.data_type);

    if (draftValue === attr.value || updateMutation.isPending) {
      setEditingId(null);
      return;
    }

    try {
      if (attr.value_id) {
        // Update existing AttributeValue using its ID

        // Only use locale/channel if the attribute supports them
        const updateLocale =
          attr.is_localisable && selectedLocale !== 'default' ? selectedLocale : undefined;
        const updateChannel =
          attr.is_scopable && selectedChannel !== 'default' ? selectedChannel : undefined;

        // Make direct API call with proper locale/channel filtering
        const params: Record<string, string> = {};
        if (updateLocale) {
          params.locale = updateLocale;
        }
        if (updateChannel) {
          params.channel = updateChannel;
        }

        await axiosInstance.patch(
          `/api/products/${productId}/attributes/${attr.value_id}/`,
          { value },
          { params }
        );

        // Manually trigger the same invalidations as the mutation would
        queryClient.invalidateQueries({
          queryKey: ['productAttributes', productId],
        });
        queryClient.invalidateQueries({
          queryKey: ['product', parseInt(productId), 'effectiveAttributes'],
        });
      } else {
        // Create new AttributeValue for this attribute

        // Only send locale/channel if the attribute supports them
        const createLocale =
          attr.is_localisable && selectedLocale !== 'default' ? selectedLocale : undefined;
        const createChannel =
          attr.is_scopable && selectedChannel !== 'default' ? selectedChannel : undefined;

        await createMutation.mutateAsync({
          attributeId: attr.id,
          value: value,
          locale: createLocale,
          channel: createChannel,
        });
      }
      setEditingId(null);
      toast.success(getAttributesLabels.notifications.attributeUpdated);
      refetch(); // Refresh the effective attributes
    } catch (error) {
      logError(
        'Error saving attribute',
        {
          component: 'EffectiveAttributesView',
          action: 'handleSave',
          metadata: { error, attributeId: attr.id },
        },
        { showToast: false, userFacing: false }
      );
      const friendlyError = formatErrorMessage(error, attr.data_type);
      setValidationError(friendlyError);
      toast.error(`Failed to save: ${friendlyError}`);
    }
  };

  const handleLocaleChange = (newLocale: string) => {
    setSelectedLocale(newLocale as LocaleCode);
  };

  const handleChannelChange = (newChannel: string) => {
    setSelectedChannel(newChannel as ChannelCode);
  };

  const renderAttributeValue = (attr: EffectiveAttribute) => {
    if (editingId === `${attr.id}`) {
      return (
        <div className="flex items-center gap-2">
          {renderAttributeEditor(
            attr,
            draftValue,
            setDraftValue,
            updateMutation.isPending || mediaUploading,
            validationError ?? undefined
          )}
          <Button
            size="sm"
            onClick={() => handleSave(attr)}
            disabled={updateMutation.isPending || mediaUploading}
            data-testid="save-button"
          >
            <Save className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditingId(null)}
            data-testid="cancel-button"
          >
            <X className="size-4" />
          </Button>
        </div>
      );
    }

    return (
      <div className="group flex items-center justify-between">
        <div className="flex-1">
          {attr.value !== null && attr.value !== undefined ? (
            <div className="flex items-center gap-1">
              <span className={attr.overridden ? 'font-medium' : 'text-muted-foreground'}>
                {formatAttributeValue(attr)}
              </span>
              {(attr as any).required && (
                <span
                  className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-red-700"
                  aria-label="Required attribute"
                >
                  {getAttributesLabels.badges.required}
                </span>
              )}
            </div>
          ) : (
            <span className="italic text-muted-foreground">
              {getAttributesLabels.effectiveView.noData}
            </span>
          )}
          {attr.locale && (
            <span className="ml-2 rounded bg-blue-100 px-1 text-xs text-blue-800">
              {attr.locale}
            </span>
          )}
          {attr.channel && (
            <span className="ml-1 rounded bg-green-100 px-1 text-xs text-green-800">
              {attr.channel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <PermissionGuard permission="product.change">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingId(`${attr.id}`);
                    // Initialize draft value based on attribute type
                    if (attr.data_type === 'price') {
                      setDraftValue(attr.value || { amount: '', currency: '' });
                    } else if (attr.data_type === 'measurement') {
                      // For measurement, use effective unit (stored unit or default unit)
                      const currentValue = attr.value || {
                        amount: '',
                        unit: '',
                      };
                      const effectiveUnit = currentValue.unit || attr.default_unit || '';
                      setDraftValue({
                        amount: currentValue.amount || '',
                        unit: effectiveUnit,
                      });
                    } else if (attr.data_type === 'date') {
                      // For date attributes, format the value for date input
                      setDraftValue(formatDateForInput(attr.value));
                    } else if (attr.data_type === 'boolean') {
                      // For boolean attributes, ensure we have a proper boolean value
                      setDraftValue(Boolean(attr.value));
                    } else {
                      setDraftValue(String(attr.value || ''));
                    }
                  }}
                  data-testid="edit-button"
                  aria-label={`Edit ${attr.label} attribute value`}
                  className="focus:opacity-100"
                >
                  <PencilIcon className="size-4" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {getAttributesLabels.panel.tooltips.editAttribute.replace('{{label}}', attr.label)}
              </TooltipContent>
            </Tooltip>
          </PermissionGuard>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="loading-skeleton">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-5 animate-pulse rounded bg-gray-200" />
            <div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
            <div className="h-5 w-32 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
        </div>

        {/* Attribute groups skeleton */}
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-lg border">
            <div className="border-b p-4">
              <div className="h-6 w-40 animate-pulse rounded bg-gray-200" />
            </div>
            <div className="space-y-3 p-4">
              {[1, 2, 3].map(j => (
                <div key={j} className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
                    <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
                  </div>
                  <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 py-12 text-center">
        <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-red-100">
          <X className="size-8 text-red-600" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {getAttributesLabels.effectiveView.error}
          </h3>
          <p className="mx-auto max-w-md text-sm text-gray-600">
            {getAttributesLabels.effectiveView.noData}
          </p>
          {error instanceof Error && (
            <details className="mt-2 text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700">
                {t('productdetailpage.ui.common.error', 'Error')}
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-gray-50 p-2 text-left">
                {error.message}
              </pre>
            </details>
          )}
        </div>
        <div className="flex justify-center gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <Save className="mr-2 size-4" />
            {t('productdetailpage.ui.common.retry', 'Try Again')}
          </Button>
          <Button onClick={() => window.location.reload()} variant="ghost" size="sm">
            {t('productdetailpage.ui.common.refresh', 'Refresh')}
          </Button>
        </div>
      </div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <div className="space-y-4 py-12 text-center">
        <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-blue-50">
          <Layers className="size-8 text-blue-600" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {getAttributesLabels.panel.noGroups}
          </h3>
          <p className="mx-auto max-w-md text-sm text-gray-600">
            {getAttributesLabels.panel.emptyState.description}
          </p>
          <div className="space-y-1 text-xs text-gray-500">
            <p>{getAttributesLabels.panel.emptyState.createMode}</p>
          </div>
        </div>
      </div>
    );
  }

  // Get all existing attribute IDs for the AddAttributeButton (currently unused but may be needed for future features)
  // const existingAttributeIds = groups.flatMap(g => g.attributes.map(a => a.id));

  return (
    <div className="space-y-6">
      {/* Header with locale/channel selector */}
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 dark:border-blue-800 dark:from-blue-900/30 dark:to-blue-800/30">
            <Settings className="size-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {getAttributesLabels.panel.title}
            </h3>
            <p className="text-sm text-muted-foreground">
              {getAttributesLabels.effectiveView.description}
            </p>
          </div>
        </div>
        <LocaleChannelSelector
          selectedLocale={selectedLocale}
          selectedChannel={selectedChannel}
          onLocaleChange={handleLocaleChange}
          onChannelChange={handleChannelChange}
        />
      </div>

      {validationError && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {validationError}
        </div>
      )}

      {/* Attribute Groups */}
      <Accordion
        type="multiple"
        defaultValue={groups.map(g => `group-${g.groupId}`)}
        className="space-y-2"
      >
        {groups.map(group => (
          <AccordionItem
            key={group.groupId}
            value={`group-${group.groupId}`}
            className="rounded-lg border"
          >
            <AccordionTrigger
              className="px-4 py-3 hover:no-underline"
              aria-label={`Toggle ${group.groupName} attribute group`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{group.groupName}</span>
                {group.required && (
                  <span
                    className="rounded bg-red-100 px-2 py-1 text-xs text-red-800"
                    aria-label="Required attribute group"
                  >
                    {getAttributesLabels.badges.required}
                  </span>
                )}
                <span
                  className="text-sm text-gray-500"
                  aria-label={`${group.attributes.length} attributes in this group`}
                >
                  (
                  {group.attributes.length === 1
                    ? getAttributesLabels.labels.attributeCount.replace(
                        '{{count}}',
                        String(group.attributes.length)
                      )
                    : getAttributesLabels.labels.attributeCountPlural.replace(
                        '{{count}}',
                        String(group.attributes.length)
                      )}
                  )
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <Table role="table" aria-label={`Attributes in ${group.groupName} group`}>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">{getAttributesLabels.panel.tableHeaders.name}</TableHead>
                    <TableHead scope="col">{getAttributesLabels.panel.tableHeaders.type}</TableHead>
                    <TableHead scope="col">
                      {getAttributesLabels.panel.tableHeaders.value}
                    </TableHead>
                    <TableHead scope="col">
                      {t('productdetailpage.productAttributes.panel.tableHeaders.status', 'Status')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.attributes.map(attr => (
                    <TableRow key={attr.id} className="focus-within:bg-gray-50">
                      <TableCell scope="row">
                        <div>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{attr.label}</span>
                            {(group.required ||
                              (attr as any).required ||
                              (attr as any).is_required) && (
                              <span
                                className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-red-700"
                                aria-label="Required attribute"
                              >
                                {getAttributesLabels.badges.required}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{attr.code}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className="rounded bg-muted/50 px-2 py-1 text-sm text-muted-foreground dark:bg-muted/30"
                          aria-label={`Attribute type: ${attr.data_type}`}
                        >
                          {translateAttributeType(attr.data_type, t)}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-md">{renderAttributeValue(attr)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {attr.overridden ? (
                            <div
                              className="flex items-center gap-1 text-green-600 dark:text-green-400"
                              aria-label="This attribute has a custom value"
                            >
                              <Eye className="size-4" aria-hidden="true" />
                              <span className="text-sm">
                                {getAttributesLabels.effectiveView.overridden}
                              </span>
                            </div>
                          ) : (
                            <div
                              className="flex items-center gap-1 text-muted-foreground"
                              aria-label="This attribute inherits its value from the family"
                            >
                              <EyeOff className="size-4" aria-hidden="true" />
                              <span className="text-sm">
                                {getAttributesLabels.effectiveView.inherited}
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
